import { useRef, useState } from 'react';
import {
  ActionIcon,
  Avatar,
  Box,
  Group,
  Paper,
  Rating,
  Stack,
  Text,
} from '@mantine/core';
import {
  animate,
  motion,
  useMotionValue,
  useTransform,
  type PanInfo,
} from 'motion/react';
import {
  TbBell,
  TbCards,
  TbCheck,
  TbMessageCircle,
  TbThumbUp,
  TbX,
} from 'react-icons/tb';
import useNotification from '@hooks/useNotification';
import type { FlipReview } from '@th-types/flip-review.type';
import {
  stackedCardStyle,
  stampBoxStyle,
  stampWrapperStyle,
  topCardBaseStyle,
} from './FlipReviewsWidget.styles';

export interface FlipReviewsWidgetProps {
  reviews: FlipReview[];
  onHelpful: (id: number) => void | Promise<void>;
  onUnhelpful: (id: number) => void | Promise<void>;
}

const SWIPE_THRESHOLD = 180;
// Distance the dismissed card travels off-screen before it leaves the stack.
const EXIT_X = 400;
const CARD_HEIGHT = 380;
const LIKE_COLOR = '#5cca73';
const NOPE_COLOR = '#9a9b9c';
const BRAND_YELLOW = 'var(--yellow)';

enum SwipeDirection {
  Like = 'like',
  Nope = 'nope',
}

function FlipCardContent({
  review,
  onHelpful,
  onUnhelpful,
  showActions,
  cardOpacity,
}: {
  review: FlipReview;
  onHelpful?: () => void;
  onUnhelpful?: () => void;
  showActions?: boolean;
  cardOpacity?: ReturnType<typeof useTransform<number, number>>;
}) {
  return (
    <motion.div style={{ height: '100%', opacity: cardOpacity }}>
      <Paper shadow="md" p="md" h="100%" bg="white">
        <Stack gap="md" h="100%" justify="space-between">
          <Stack gap="md">
            <Group align="center" wrap="nowrap" gap="md">
              <Avatar src={review.profileImageUrl} size={70} radius="md" />
              <Stack gap={4} flex={1} justify="center">
                <Text fw={700} fz={18} lh={1.2}>
                  {review.companyName}
                </Text>
                <Text fz={13} c="dimmed" lh={1.2}>
                  {review.showRankingPosition &&
                    review.rankingPosition !== null &&
                    `#${review.rankingPosition} | `}
                  {review.companyType}
                </Text>
                <Rating
                  value={review.rating}
                  readOnly
                  fractions={2}
                  size="xs"
                />
              </Stack>
            </Group>
            <Stack gap="xs">
              <Text fw={700} fz={15}>
                {review.jobTitle}
              </Text>
              <Text fz={15} lh={1.4} c="#212932">
                “{review.comments}”
              </Text>
            </Stack>
          </Stack>
          {showActions && (
            <Stack align="center" gap="xs">
              <Text fz={15} c="#212932">
                Was this review helpful?
              </Text>
              <Group gap={40} justify="center">
                <ActionIcon
                  variant="outline"
                  color="gray"
                  size={50}
                  radius="xl"
                  onClick={onUnhelpful}
                  aria-label="Not helpful"
                >
                  <TbX size={22} />
                </ActionIcon>
                <ActionIcon
                  variant="outline"
                  color={LIKE_COLOR}
                  size={50}
                  radius="xl"
                  onClick={onHelpful}
                  aria-label="Helpful"
                >
                  <TbCheck size={22} />
                </ActionIcon>
              </Group>
            </Stack>
          )}
        </Stack>
      </Paper>
    </motion.div>
  );
}

function EmptyState() {
  return (
    <Paper shadow="md" p="md" h={CARD_HEIGHT} bg="white">
      <Stack h="100%" justify="center" gap="xl" px="md">
        <Stack align="center" gap="xs">
          <Group gap="xs" justify="center" wrap="nowrap">
            <Text fw={700} fz={18} ta="center">
              That&apos;s All for Today!
            </Text>
            <TbCards size={26} color={BRAND_YELLOW} />
          </Group>
          <Text fz={15} c="dimmed" ta="center">
            Come back tomorrow and we&apos;ll reload 25 new flips
          </Text>
        </Stack>
        <Stack gap="md">
          <Group gap="md" align="center" wrap="nowrap">
            <TbThumbUp
              size={24}
              color={BRAND_YELLOW}
              style={{ flexShrink: 0 }}
            />
            <Text fz={14}>Thanks for making our industry better!</Text>
          </Group>
          <Group gap="md" align="center" wrap="nowrap">
            <TbBell size={24} color={BRAND_YELLOW} style={{ flexShrink: 0 }} />
            <Text fz={14}>
              Workers are notified when their review is found helpful
            </Text>
          </Group>
          <Group gap="md" align="flex-start" wrap="nowrap">
            <TbMessageCircle
              size={24}
              color={BRAND_YELLOW}
              style={{ flexShrink: 0 }}
            />
            <Stack gap={0}>
              <Text fz={14}>Get your Review in Flip!</Text>
              <Text fz={13} c="dimmed">
                Add, rate and leave a comment on any promo you worked the last
                365 days
              </Text>
            </Stack>
          </Group>
        </Stack>
      </Stack>
    </Paper>
  );
}

function ChoiceStamp({
  text,
  color,
  opacity,
}: {
  text: string;
  color: string;
  opacity: ReturnType<typeof useTransform<number, number>>;
}) {
  return (
    <motion.div style={{ ...stampWrapperStyle, opacity }}>
      <Box style={stampBoxStyle(color)}>{text}</Box>
    </motion.div>
  );
}

function FlipDeck({ reviews, onHelpful, onUnhelpful }: FlipReviewsWidgetProps) {
  const [stack, setStack] = useState(reviews);
  const { showErrorNotification } = useNotification();
  const isDismissing = useRef(false);
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-300, 300], [-25, 25]);
  const likeOpacity = useTransform(x, [0, SWIPE_THRESHOLD, EXIT_X], [0, 1, 0]);
  const nopeOpacity = useTransform(
    x,
    [-EXIT_X, -SWIPE_THRESHOLD, 0],
    [0, 1, 0]
  );
  const cardOpacity = useTransform(
    x,
    [-SWIPE_THRESHOLD, 0, SWIPE_THRESHOLD],
    [0, 1, 0]
  );

  if (stack.length === 0) {
    return <EmptyState />;
  }

  const top = stack[stack.length - 1];

  const dismiss = (direction: SwipeDirection) => {
    if (isDismissing.current) return;
    isDismissing.current = true;
    const review = top;
    const isLike = direction === SwipeDirection.Like;
    const action = isLike ? onHelpful : onUnhelpful;
    animate(x, isLike ? EXIT_X : -EXIT_X, {
      duration: 0.3,
      onComplete: () => {
        setStack((current) =>
          current.filter((currentReview) => currentReview.id !== review.id)
        );
        x.set(0);
        isDismissing.current = false;
        Promise.resolve(action(review.id)).catch(() => {
          setStack((current) => [review, ...current]);
          showErrorNotification(
            'Could not save your response. Please try again.'
          );
        });
      },
    });
  };

  const handleDragEnd = (_event: PointerEvent, info: PanInfo) => {
    if (info.offset.x >= SWIPE_THRESHOLD) {
      dismiss(SwipeDirection.Like);
    } else if (info.offset.x <= -SWIPE_THRESHOLD) {
      dismiss(SwipeDirection.Nope);
    }
  };

  return (
    <Box pos="relative" h={CARD_HEIGHT}>
      {stack.slice(-2).map((review, index, slice) => {
        const isTop = index === slice.length - 1;
        const depth = slice.length - 1 - index;

        if (!isTop) {
          return (
            <Box
              key={review.id}
              pos="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              style={stackedCardStyle(depth, index)}
            >
              <FlipCardContent review={review} />
            </Box>
          );
        }

        return (
          <motion.div
            key={review.id}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.7}
            onDragEnd={handleDragEnd}
            style={{ ...topCardBaseStyle, x, rotate, zIndex: index }}
            whileTap={{ cursor: 'grabbing' }}
          >
            <FlipCardContent
              review={review}
              showActions
              onHelpful={() => dismiss(SwipeDirection.Like)}
              onUnhelpful={() => dismiss(SwipeDirection.Nope)}
              cardOpacity={cardOpacity}
            />
            <ChoiceStamp text="LIKE" color={LIKE_COLOR} opacity={likeOpacity} />
            <ChoiceStamp text="NOPE" color={NOPE_COLOR} opacity={nopeOpacity} />
          </motion.div>
        );
      })}
    </Box>
  );
}

export function FlipReviewsWidget({
  reviews,
  onHelpful,
  onUnhelpful,
}: FlipReviewsWidgetProps) {
  return (
    <Box>
      <FlipDeck
        reviews={reviews}
        onHelpful={onHelpful}
        onUnhelpful={onUnhelpful}
      />
    </Box>
  );
}

export default FlipReviewsWidget;
